import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@clay-run/glaze/button';
import { ComboboxField } from '@clay-run/glaze/combobox-field';
import { ComboboxOption } from '@clay-run/glaze/combobox';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';
import { Card } from '@clay-run/glaze/card';
import { TextareaField } from '@clay-run/glaze/textarea-field';
// `/toast` has no flat `dist/toast.d.ts` — see index.tsx's import comment.
import { addToast } from '@clay-run/glaze/toast/index';
import { addApiErrorToast } from '../../_shared/api-error-toast';
import { useApi, useCurrentUser } from '../../_shared/hooks';
import { isInCampaignWindow } from '../campaign-window';
import { memberPrefillFields, type CampaignMember } from '../campaign-members';
import {
  LEAD_SCANNER_CAMPAIGNS_QUERY_KEY,
  campaignWhenAndWhere,
  fetchCampaigns,
  type Campaign,
} from '../campaigns';
import { useScanApi, useScansQuery } from '../hooks/useScanApi';
import { useCompleteIntake } from '../hooks/useCompleteIntake';
import { mappingsQueryOptions } from '../rep-mapping-client';
import { intakeBatchProgress, useLeadScannerStore } from '../store';
import type { ScansFilters } from '../scans-url-state';
import PhotoIntake, { type PhotoIntakeHandle } from './PhotoIntake';
import LinkedInQrScanner from './LinkedInQrScanner';
import CampaignMemberSearch from './CampaignMemberSearch';
import ScanListCard from './ScanListCard';

/**
 * Home (R5, U4 of plans/2026-08-25-001-lead-scanner-implementation-plan.md):
 * mapped-event picker and the intake actions. Rendered both as its own
 * mobile screen (narrow) and as the wide layout's left rail
 * (`ViewSwitcher.tsx`) — the SAME component instance either way, which is
 * why the event-selection state below is plain component state rather than
 * being duplicated per-surface.
 *
 * The collapsed Recent scans accordion moved to the unified Scans page (plan
 * plans/2026-08-26-001-lead-scanner-scans-page-plan.md's `ScansPage.tsx`) —
 * one filterable, URL-shareable list replacing this component's old embedded
 * lists and the separate admin-only `AllSubmissions.tsx` (deleted). That page
 * remains the place for adjustable filtering (by rep, status, campaign,
 * search).
 *
 * Home itself keeps a "Scans" card showing every one of the viewer's own
 * scans, every status, across every campaign (QA-fix batch, Fix 1 — this used
 * to be a fixed "Draft scans" card hard-restricted to `isUnfinishedScan`
 * rows, which meant a `synced`/`submitting` scan simply disappeared from
 * Home once it left that status with no way back short of the Scans page).
 * It shares `ScanListCard.tsx` with the Scans page — same filtering
 * (`applyScanFilters`), same row rendering — but shows only status + search
 * (no submitter/campaign combos: "mine" is implicit here, and Home has no
 * cross-campaign scope to filter by), backed by plain local `useState`
 * (`homeScanFilters` below) rather than the Scans page's URL-backed nuqs
 * state, since Home isn't a distinct URL route. Default status filter is
 * "All statuses" — Home shows the viewer's whole scan history, not just
 * unfinished work. Alongside it, a bulk-photo-batch progress bar shows while
 * a multi-scan intake is still resolving. Home is left with exactly the
 * things unique to it: picking an event for this session, starting a new
 * scan, and showing the rep their own scans.
 *
 * Copy is verbatim from docs/lead-scanner-prototype.html where a Phase-1
 * equivalent exists; Phase 1 dropped the prototype's third "Generate with
 * text" intake button (free-text-blob parsing was Phase 2, out of scope
 * then), so a couple of empty-state lines were adapted rather than quoted
 * verbatim (noted at each spot). That button is now built — U11 of
 * plans/2026-08-25-002-lead-scanner-round2-plan.md — as `TextIntakeSection`
 * below.
 */

export default function Home() {
  const { apiFetch } = useApi();
  const { user } = useCurrentUser();
  const currentUserEmail = user?.ownerEmail ?? user?.email ?? null;

  const completeIntake = useCompleteIntake();

  // Explicit event override for this session — `null` means "use the
  // default" (first in-window mapped event), never a hardcoded id.
  const [sessionCampaignId, setSessionCampaignId] = useState<string | null>(null);

  // Home's own filter state for the shared `ScanListCard` (Fix 1) — plain
  // local state, not nuqs: Home isn't a distinct URL route, so there is
  // nothing here that needs to survive a share/reload. `mine: true`/
  // `rep: null` are fixed (never changed by `onFiltersChange`, since Home
  // shows no submitter combo) so "mine" can never drift from what the Scans
  // page means by it. `status: null` = "All statuses" default, per the
  // product-owner ask that Home show every scan, not just unfinished ones.
  const [homeScanFilters, setHomeScanFilters] = useState<ScansFilters>({
    mine: true,
    rep: null,
    status: null,
    campaign: null,
    q: '',
  });

  const mappingsQuery = useQuery(mappingsQueryOptions(apiFetch));

  const campaignsQuery = useQuery({
    queryKey: LEAD_SCANNER_CAMPAIGNS_QUERY_KEY,
    queryFn: async () => {
      // Sanctioned cross-app HTTP read, same as the mapper Settings screen —
      // the browser calls launcher's own route directly, same-origin, under
      // the rep's own auth (Context: "Launcher HTTP reads").
      return fetchCampaigns(apiFetch);
    },
  });

  const scanApi = useScanApi();

  const createScan = useMutation({ mutationFn: scanApi.createScan });

  // Live-QA round-3 bug 1: after any scan(s) are created, the rep sees them
  // immediately on Home instead of being navigated to the Scans page — this
  // is the SAME query `ViewSwitcher.tsx` and `useScansQuery` share (same
  // key), so this costs zero extra requests.
  const scansQuery = useScansQuery(currentUserEmail);
  const intakeBatchIds = useLeadScannerStore((s) => s.intakeBatchIds);
  const openScan = useLeadScannerStore((s) => s.openScan);

  const scans = scansQuery.data ?? [];
  const batchProgress = intakeBatchProgress(intakeBatchIds, scans);

  const campaigns = campaignsQuery.data ?? [];

  // ALL of the rep's mapped campaign ids, regardless of the campaign window —
  // used only as a hint on picker options (U15/R16); never a gate. `mine`
  // below is the in-window subset used for the default derivation.
  const mappedCampaignIds = useMemo(() => {
    if (!currentUserEmail) return new Set<string>();
    return new Set(
      (mappingsQuery.data ?? []).filter((m) => m.email === currentUserEmail).map((m) => m.campaignId),
    );
  }, [currentUserEmail, mappingsQuery.data]);

  const mine = useMemo(() => {
    const now = new Date();
    const campaignsById = new Map(campaigns.map((c) => [c.id, c]));
    return Array.from(mappedCampaignIds)
      .map((id) => campaignsById.get(id))
      .filter((c): c is Campaign => c != null && isInCampaignWindow(c.eventDate, now))
      .sort((a, b) => (a.eventDate ?? '').localeCompare(b.eventDate ?? ''));
  }, [campaigns, mappedCampaignIds]);

  // Default = first in-window mapped event, never hardcoded; a manual
  // pick of ANY existing campaign overrides it for the session (mapping is
  // a default source, never a gate — U15/R16).
  const activeCampaignId = sessionCampaignId ?? mine[0]?.id ?? null;
  useEffect(() => {
    if (sessionCampaignId && campaignsQuery.data && !campaignsQuery.data.some((c) => c.id === sessionCampaignId)) {
      // The previously-picked event disappeared from the campaign list
      // entirely (not merely out-of-window or unmapped) — fall back to the
      // default rather than keep a dangling selection.
      setSessionCampaignId(null);
    }
  }, [campaignsQuery.data, sessionCampaignId]);

  // The QR scanner's camera-unavailable fallback opens THIS picker rather
  // than a second one of its own (U12) — see `PhotoIntakeHandle`.
  const photoIntakeRef = useRef<PhotoIntakeHandle>(null);

  function runIntake(ids: string[]) {
    completeIntake(ids);
  }

  async function handleAddManually() {
    if (!activeCampaignId || !currentUserEmail) return;
    const scan = await createScan.mutateAsync({
      idempotencyKey: crypto.randomUUID(),
      sourceType: 'manual',
      status: 'draft',
      campaignId: activeCampaignId,
      scannedByEmail: currentUserEmail,
    });
    runIntake([scan.id]);
  }

  // Home's "search an existing campaign member" pick (Part 3 of the
  // campaign-member-search extraction) — mirrors `handleAddManually` above
  // exactly, except the new draft is born with the member's identity already
  // on it (`memberPrefillFields`, the same single source `ScanReview`'s
  // `handleLinkMember` uses) rather than created blank. Reuses the exact same
  // `createScan` mutation and `runIntake` navigation `handleAddManually`
  // already uses to open a freshly created scan — no new opening logic.
  async function handleMemberPick(member: CampaignMember) {
    if (!activeCampaignId || !currentUserEmail) return;
    const scan = await createScan.mutateAsync({
      idempotencyKey: crypto.randomUUID(),
      sourceType: 'manual',
      status: 'draft',
      campaignId: activeCampaignId,
      scannedByEmail: currentUserEmail,
      ...memberPrefillFields(member),
    });
    runIntake([scan.id]);
  }

  const isLoading = mappingsQuery.isLoading || campaignsQuery.isLoading;

  return (
    <div className="flex flex-col gap-4">
      <EventSection
        campaigns={campaigns}
        mine={mine}
        mappedCampaignIds={mappedCampaignIds}
        activeCampaignId={activeCampaignId}
        onSelect={setSessionCampaignId}
        onMemberPick={handleMemberPick}
      />

      <Card padded className="flex flex-col gap-2">
        <Text size="sm" weight="semibold">
          Add lead(s)
        </Text>
        <div className="flex flex-col gap-2">
          <PhotoIntake
            ref={photoIntakeRef}
            campaignId={activeCampaignId}
            scannedByEmail={currentUserEmail}
            disabled={isLoading}
          />
          {/* Second slot, per the plan's intake-button order (Decision 14):
              Scan badges → Scan LinkedIn QR → … → Add manually. */}
          <LinkedInQrScanner
            campaignId={activeCampaignId}
            scannedByEmail={currentUserEmail}
            disabled={isLoading}
            // Camera unavailable → route the rep into the picker above,
            // whose pipeline already QR-decodes a still photo (U5). Reaching
            // that picker rather than growing a second one is why
            // `PhotoIntake` exposes a handle at all.
            onUsePhotoFallback={() => photoIntakeRef.current?.openPicker()}
          />
          {/* Third slot, per the plan's intake-button order (Decision 14):
              Scan badges → Scan LinkedIn QR → Generate with text → Add
              manually. */}
          <TextIntakeSection
            campaignId={activeCampaignId}
            scannedByEmail={currentUserEmail}
            disabled={isLoading}
            onIntake={runIntake}
          />
          <Button
            variant="secondary"
            size="lg"
            disabled={!activeCampaignId || isLoading}
            loading={createScan.isPending}
            onClick={handleAddManually}
          >
            Add manually
          </Button>
        </div>
      </Card>

      {/* "Scans" card (Fix 1) — the viewer's own scans, every status, every
          campaign, filterable by status + search (no submitter/campaign
          combos — see this component's file header). Shares `ScanListCard`
          with the Scans page rather than a second, drifted implementation. */}
      <ScanListCard
        scans={scans}
        isLoading={scansQuery.isLoading}
        me={currentUserEmail}
        filters={homeScanFilters}
        onFiltersChange={(patch) => setHomeScanFilters((prev) => ({ ...prev, ...patch }))}
        campaigns={campaigns}
        onOpenScan={openScan}
        onDeleteScan={(id) => scanApi.deleteScan(id)}
        batchProgress={batchProgress}
      />
    </div>
  );
}

interface EventSectionProps {
  // FULL campaign list — the picker is always-real, never gated to a
  // mapped-only subset (U15/R16: mapping is a default source, never a gate).
  campaigns: Campaign[];
  // In-window mapped events only — used to bucket the sort ("default
  // candidates" surface first), not to filter what's selectable.
  mine: Campaign[];
  // ALL of the rep's mapped campaign ids (unfiltered by window) — drives the
  // "mapped" hint on individual options.
  mappedCampaignIds: Set<string>;
  activeCampaignId: string | null;
  onSelect: (id: string) => void;
  onMemberPick: (member: CampaignMember) => void;
}

function EventSection({ campaigns, mine, mappedCampaignIds, activeCampaignId, onSelect, onMemberPick }: EventSectionProps) {
  const [eventQuery, setEventQuery] = useState('');

  const activeCampaign = useMemo(
    () => campaigns.find((c) => c.id === activeCampaignId) ?? null,
    [campaigns, activeCampaignId],
  );

  // Sort: in-window mapped events first (the default candidates, in their
  // existing ascending-date order), then the rest by event date descending
  // so current/recent events surface over old ones.
  const sortedCampaigns = useMemo(() => {
    const mineIds = new Set(mine.map((c) => c.id));
    const defaults = campaigns.filter((c) => mineIds.has(c.id));
    const rest = campaigns
      .filter((c) => !mineIds.has(c.id))
      .sort((a, b) => (b.eventDate ?? '').localeCompare(a.eventDate ?? ''));
    return [...defaults, ...rest];
  }, [campaigns, mine]);

  const trimmedQuery = eventQuery.trim().toLowerCase();
  const filteredCampaigns = trimmedQuery
    ? sortedCampaigns.filter((c) => `${c.name} ${c.city ?? ''}`.toLowerCase().includes(trimmedQuery))
    : sortedCampaigns;

  return (
    <Card padded className="flex flex-col gap-2">
      <Text size="sm" weight="semibold">
        Event
      </Text>
      <ComboboxField
        variant="popover"
        aria-label="Choose event"
        placeholder="Choose the event you're scanning at"
        searchPlaceholder="Search events…"
        items={filteredCampaigns}
        itemToStringLabel={(c: Campaign) => c.name}
        value={activeCampaign}
        onValueChange={(c) => c && onSelect(c.id)}
        inputValue={eventQuery}
        onInputValueChange={setEventQuery}
        by="id"
      >
        {filteredCampaigns.map((c) => (
          <ComboboxOption key={c.id} value={c}>
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate">
                {c.name} — {campaignWhenAndWhere(c)}
              </span>
              {mappedCampaignIds.has(c.id) && (
                <StatusBadge status="neutral" size="xs">
                  mapped
                </StatusBadge>
              )}
            </span>
          </ComboboxOption>
        ))}
      </ComboboxField>
      {/* Search/pick an existing campaign member right here on Home — picking
          one creates and opens a new draft scan prefilled with their info
          (`handleMemberPick`). Gated on an active campaign existing at all
          (including Home's own auto-selected default, `mine[0]?.id` —
          deliberately not gated on an EXPLICIT dropdown pick: every other
          Home intake action already treats the default the same as an
          explicit pick, so this shouldn't be the one exception). */}
      {activeCampaignId != null && (
        <CampaignMemberSearch
          campaignId={activeCampaignId}
          linkedMember={null}
          onLink={onMemberPick}
          onUnlink={() => {}}
        />
      )}
    </Card>
  );
}

interface TextIntakeSectionProps {
  campaignId: string | null;
  scannedByEmail: string | null;
  disabled?: boolean;
  onIntake: (ids: string[]) => void;
}

/**
 * "Generate with text" (U11, R11 of
 * plans/2026-08-25-002-lead-scanner-round2-plan.md) — the third intake
 * action. Entry surface is an inline-expanding textarea, never a `Modal`
 * (Spencer's explicit call, U11's Deferred-to-Implementation note): tapping
 * the collapsed button swaps it for a `TextareaField` + primary "Generate
 * leads" button in place, right inside the same "Add lead(s)" card the other
 * two intake actions live in.
 *
 * The route (`POST /scans/from-text`) resolves synchronously — no
 * `processing` intermediate state to poll (Key Technical Decision 11) — so
 * this component's only async state is the one in-flight request.
 */
function TextIntakeSection({ campaignId, scannedByEmail, disabled, onIntake }: TextIntakeSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const { createScansFromText } = useScanApi();

  const ready = campaignId != null && scannedByEmail != null && !disabled;
  const trimmedText = text.trim();

  function collapse() {
    setExpanded(false);
    setText('');
  }

  async function handleGenerate() {
    if (campaignId === null || scannedByEmail === null || trimmedText.length === 0) return;

    setIsGenerating(true);
    try {
      const scans = await createScansFromText({ text: trimmedText, campaignId, scannedByEmail });
      if (scans.length === 0) {
        // A well-formed "no leads named in this text" result — not an error,
        // per the route's own 200-with-empty-array contract.
        addToast({ content: 'No leads found in that text', status: 'info' });
        return;
      }
      collapse();
      onIntake(scans.map((s) => s.id));
    } catch (err) {
      addApiErrorToast({ content: 'Couldn’t generate leads from that text.', error: err });
    } finally {
      setIsGenerating(false);
    }
  }

  if (!expanded) {
    return (
      <Button variant="secondary" size="lg" disabled={!ready} onClick={() => setExpanded(true)}>
        Generate with text
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <TextareaField
        aria-label="Paste your notes"
        placeholder="Paste your notes from the event — one or more leads, in your own words."
        value={text}
        onValueChange={setText}
        rows={5}
        disabled={isGenerating}
      />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={collapse} disabled={isGenerating}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!ready || trimmedText.length === 0}
          loading={isGenerating}
          onClick={handleGenerate}
        >
          Generate leads
        </Button>
      </div>
    </div>
  );
}
